import React from "react";
import {
  ElementType,
  ElementViewProps,
  ImageActionType,
  ImageSettings,
  ImageSize,
} from "@/shared/types";
import { backgroundImageOverlayStyles } from "@/shared/utils/styles";
import { LazyLightBox } from "@/shared/Components/LightBox";
import { ElementPlaceholder } from "@/shared/Components/ElementPlaceholder/ElementPlaceholder";

export interface ImageViewProps extends ElementViewProps {}

const ImageView: React.FC<ImageViewProps> = ({
  element,
  elementRef,
  elementSize,
  elementContentRef,
  wuiltContext,
}) => {
  const imageSettings = element?.settings as ImageSettings;
  const contextElements = wuiltContext?.base;
  const imageActionValue = imageSettings?.content?.action?.value;
  const shouldOpenInANewTab =
    imageSettings?.content?.action?.shouldOpenInANewTab;

  const [showLightBox, setShowLightBox] = React.useState(false);

  const imageSrc = imageSettings?.content?.src;
  const shouldImageFill = imageSettings?.design?.size === ImageSize.Cover;

  const shouldImageDoNothing =
    imageSettings?.content?.action?.type === ImageActionType.DoNothing;

  const shouldImageScaleUp =
    imageSettings?.content?.action?.type === ImageActionType.ScaleUp;

  const isInternalLinkAction =
    imageSettings?.content?.action?.type === ImageActionType.InternalLink;
  const isExternalLinkAction =
    imageSettings?.content?.action?.type === ImageActionType.ExternalLink;

  const shouldImageOpenALink = isInternalLinkAction || isExternalLinkAction;

  const ActionComponent = isExternalLinkAction
    ? contextElements?.Link
    : isInternalLinkAction
    ? contextElements?.PageLink
    : "div";

  const imageActionLinkProps =
    shouldImageDoNothing || (!shouldImageOpenALink && !shouldImageScaleUp)
      ? {}
      : {
          href: imageActionValue,
          page: { id: imageActionValue },
          target: shouldOpenInANewTab ? "_blank" : "_self",
          ...(shouldImageScaleUp ? { onClick: handleScaleUpImage } : {}),
        };

  const imageStyles = imageSettings?.content?.src
    ? backgroundImageOverlayStyles(imageSettings?.design?.overlay)
    : {};

  function handleScaleUpImage() {
    setShowLightBox(true);
  }

  return (
    <div
      ref={elementRef as React.RefObject<HTMLDivElement>}
      id={element?.id}
      data-test="ImageView"
      style={{
        width: shouldImageFill || !imageSrc ? "100%" : "fit-content",
        height: shouldImageFill || !imageSrc ? "100%" : "fit-content",
      }}
    >
      <style>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {`
          .yarl__navigation_next, .yarl__navigation_prev {
            display: none;
          }
        `}
      </style>
      {imageSettings?.content?.src ? (
        <ActionComponent
          style={{
            display: "block",
            width: shouldImageFill ? "100%" : "fit-content",
            height: shouldImageFill ? "100%" : "fit-content",
            cursor:
              shouldImageOpenALink || shouldImageScaleUp
                ? "pointer"
                : undefined,
          }}
          {...imageActionLinkProps}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              borderStartStartRadius:
                imageSettings?.design?.borderRadius?.startStart,
              borderStartEndRadius:
                imageSettings?.design?.borderRadius?.startEnd,
              borderEndStartRadius:
                imageSettings?.design?.borderRadius?.endStart,
              borderEndEndRadius: imageSettings?.design?.borderRadius?.endEnd,
              ...imageStyles,
            }}
          />
          <img
            ref={elementContentRef as React.RefObject<HTMLImageElement>}
            src={imageSettings?.content?.src}
            alt={imageSettings?.content?.alt}
            title={imageSettings?.content?.alt}
            style={{
              width: shouldImageFill ? "100%" : "auto",
              maxWidth: shouldImageFill ? "100%" : elementSize?.width,
              height: shouldImageFill ? "100%" : elementSize?.height,
              maxHeight: shouldImageFill ? "100%" : elementSize?.height,
              objectPosition: "50% 50%",
              overflow: "hidden",
              objectFit: imageSettings?.design?.size,
              borderStartStartRadius:
                imageSettings?.design?.borderRadius?.startStart,
              borderStartEndRadius:
                imageSettings?.design?.borderRadius?.startEnd,
              borderEndStartRadius:
                imageSettings?.design?.borderRadius?.endStart,
              borderEndEndRadius: imageSettings?.design?.borderRadius?.endEnd,
            }}
          />
          <React.Suspense fallback={null}>
            {shouldImageScaleUp && (
              <LazyLightBox
                open={showLightBox}
                carousel={{
                  finite: true,
                  padding: 0,
                }}
                zoom={{
                  maxZoomPixelRatio: 3,
                  scrollToZoom: true,
                }}
                close={() => setShowLightBox(false)}
                slides={[{ src: imageSettings?.content?.src }]}
              />
            )}
          </React.Suspense>
        </ActionComponent>
      ) : (
        <ElementPlaceholder elementType={ElementType.Image} />
      )}
    </div>
  );
};

export { ImageView };
